import { CostDimension, isCalculationLineApi, MandatorySumDimension, OptionalSumDimensions, ViewModelLine, ViewModelLineType } from "src/app/state-management/calculation-state";
import { ExtractionMapping } from "./types/data-extraction.types";
import { CalculationLineApi, CalculationMetaApi, CalculationTailApi, ValueSetApi, ValueSetKey } from "src/app/types";
import { formatterPercentage } from "./pdf-export.constants";

type ExtractedValue = string | number;

const emptyText = '';
const emptyNumber = 0;

const getCalculationLineValue = (
    line: ViewModelLine,
    field: 'quantity' | 'unit'
): string => {
    return isCalculationLineApi(line.line)
        ? line.line[field] ?? emptyText
        : emptyText;
};

const getAggregationNumberValue = (
    line: ViewModelLine,
    dimension: CostDimension,
    field: MandatorySumDimension | OptionalSumDimensions
): number => {
    return line.aggregation?.[dimension]?.[field] ?? 0;
};

const getAggregationTextValue = (
    line: ViewModelLine,
    dimension: CostDimension,
    field: MandatorySumDimension
): string => {
    return String(line.aggregation?.[dimension]?.[field] ?? '');
};


const getPercentage = (line: ViewModelLine, meta: CalculationMetaApi, propertyName: ValueSetKey): number => {
    // Just hacking to hide percentages from provisional cost lines

    // So we will not show a percentage in the PDF if
    // 1. There is no calculation line available (e.g. this is the case for (sub)category headers)
    // 2. If the calculation line is part of the 'tail' (for lack of proper translation atm)
    // 3. If the calculation line is a text line
    if (!line.line || !isCalculationLineApi(line.line) || !!line.line.isTextLine) {
        return emptyNumber;
    }

    const prop = line.line[propertyName] as ValueSetApi;

    // Also hide percentage if the net amount is zero or missing. No use to show a percentage if the net amount is zero
    if (!prop.net) {
        return emptyNumber;
    }

    return line.line[propertyName]?.percent ?? meta?.percentages?.[propertyName] ?? emptyNumber;
}

export const TRANSLATE_CALCULATION_PATH: ExtractionMapping = {
    DESCRIPTION: (line) => line.description ?? line.line?.description ?? '',

    CODE: (line) => {
        if (!line.line) {
            return line.code ?? '';
        }

        if (isCalculationLineApi(line.line) && line.line.unit?.startsWith('spt')) {
            return 'stelpost';
        }

        return '';
    },

    QUANTITY: (line) => getCalculationLineValue(line, 'quantity'),
    UNIT: (line) => getCalculationLineValue(line, 'unit'),

    HOURS_NORM: (line) => getAggregationTextValue(line, 'hours', 'norm'),
    HOURS_TOTAL: (line) => getAggregationNumberValue(line, 'hours', 'hours'),
    HOURS_NET: (line) => getAggregationNumberValue(line, 'hours', 'net'),
    HOURS_GROSS: (line) => getAggregationNumberValue(line, 'hours', 'gross'),
    HOURS_UNIT: (line) => getAggregationNumberValue(line, 'hours', 'unitPrice'),
    HOURS_PERCENTAGE: (line, meta) => getPercentage(line, meta, 'hours'),

    MATERIAL_NORM: (line) => getAggregationTextValue(line, 'material', 'norm'),
    MATERIAL_NET: (line) => getAggregationNumberValue(line, 'material', 'net'),
    MATERIAL_GROSS: (line) => getAggregationNumberValue(line, 'material', 'gross'),
    MATERIAL_UNIT: (line) => getAggregationNumberValue(line, 'material', 'unitPrice'),
    MATERIAL_PERCENTAGE: (line, meta) => getPercentage(line, meta, 'material'),

    EQUIPMENT_NORM: (line) => getAggregationTextValue(line, 'equipment', 'norm'),
    EQUIPMENT_NET: (line) => getAggregationNumberValue(line, 'equipment', 'net'),
    EQUIPMENT_GROSS: (line) => getAggregationNumberValue(line, 'equipment', 'gross'),
    EQUIPMENT_UNIT: (line) => getAggregationNumberValue(line, 'equipment', 'unitPrice'),
    EQUIPMENT_PERCENTAGE: (line, meta) => getPercentage(line, meta, 'equipment'),

    SUBCONTRACTING_NORM: (line) => getAggregationTextValue(line, 'subcontracting', 'norm'),
    SUBCONTRACTING_NET: (line) => getAggregationNumberValue(line, 'subcontracting', 'net'),
    SUBCONTRACTING_GROSS: (line) => getAggregationNumberValue(line, 'subcontracting', 'gross'),
    SUBCONTRACTING_UNIT: (line) => getAggregationNumberValue(line, 'subcontracting', 'unitPrice'),
    SUBCONTRACTING_PERCENTAGE: (line, meta) => getPercentage(line, meta, 'subcontracting'),

    UNIT_PRICE: (line) => {
        if (line.type === ViewModelLineType.TAIL_PERCENTAGE) {
            const percent = (line.line as CalculationTailApi)?.percent ?? 0;
            return `${formatterPercentage.format(Number(percent.toFixed(5)) * 100)}%`;
        }

        return getAggregationTextValue(line, 'all', 'unitPrice');
    },

    NET_TOTAL: (line) => getAggregationNumberValue(line, 'all', 'net'),
    GROSS_TOTAL: (line) => getAggregationNumberValue(line, 'all', 'gross'),
    NR_OF_HOURS: (line) => getAggregationNumberValue(line, 'hours', 'hours'),
};

export const toNumberOrZero = (value: string | number | undefined): number => {
    if (typeof value === 'number') {
        return value;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : 0;
};